import Event1 from "../../../assets/images/how_it_works_01.webp";
import Event2 from "../../../assets/images/how_it_works_02.webp";
import Event3 from "../../../assets/images/how_it_works_03.webp";
import RocketIcon from "../../../assets/images/rocket-icon.webp";
import TeamIcon from "../../../assets/images/team-icon.webp";
import RectangleIcon from "../../../assets/images/rectangle.webp";
import Tool1 from "../../../assets/images/tool-01.webp";
import Tool2 from "../../../assets/images/tool-02.webp";
import Tool3 from "../../../assets/images/tool-03.webp";
import Image from "../../elements/Image";
import Button from "../../form/Button";
import { useTranslation } from "react-i18next";

const HomeEvent = () => {
  const { t } = useTranslation();

  const featuresData = [
    {
      title: t("Why Event Organisers Choose <strong>Dancebrite</strong>"),
      image: Event1,
      imageRight: true,
      points: [
        {
          icon: RocketIcon,
          heading: t("Create Events in Minutes"),
          text: t(
            "AI-assisted setup lets you launch stunning event pages fast — no tech skills needed."
          ),
        },
        {
          icon: TeamIcon,
          heading: t("Built for Teams"),
          text: t(
            "Easily invite co-organisers, assign roles, and collaborate in one place."
          ),
        },
        {
          icon: RectangleIcon,
          heading: t("Promote Like a Pro"),
          text: t(
            "Set up promoters, track performance, and boost ticket sales effortlessly."
          ),
        },
      ],
    },
    {
      title: t("<strong>Sell Tickets</strong> with Confidence"),
      image: Event2,
      imageRight: false,
      listHTML: `
      <ul>
        <li>• ${t("Secure, PCI-compliant payments")}</li>
        <li>• ${t("Accept all major cards and wallets")}</li>
        <li>• ${t("Instant payouts, no setup fees")}</li>
        <li>• ${t("Just £0.50 + VAT per paid ticket")}</li>
        <li>• ${t("Free to use for free events")}</li>
      </ul>
    `,
    },
    {
      title: t("Go Global"),
      image: Event3,
      imageRight: true,
      text: [
        t(
          "With Multi-language support and <br/> international payment options"
        ),
        t("<strong><i>reach dancers anywhere!</i></strong>"),
      ],
      hasButton: true,
    },
  ];

  const toolsSectionData = {
    title: t("With <strong>Tools</strong> to Power Every Part of Your Event"),
    tools: [
      {
        icon: Tool1,
        features: [
          {
            title: t("Streamlined Registrations"),
            description: t("Easy sign-ups, clear dashboards"),
          },
          {
            title: t("Mobile Check-In"),
            description: t("QR or name-based, right from your phone or tablet"),
          },
          {
            title: t("Automated Scheduling"),
            description: t("Build and publish event timetables with ease"),
          },
        ],
      },
      {
        icon: Tool2,
        features: [
          {
            title: t("Real-Time Scoring"),
            description: t("Live judging and leaderboards"),
          },
          {
            title: t("Analytics & Reports"),
            description: t(
              "Insights into signups, revenue, and participant data"
            ),
          },
          {
            title: t("Smart Messaging Tools"),
            description: t("Email, SMS & in-app notifications"),
          },
        ],
      },
      {
        icon: Tool3,
        features: [
          {
            title: t("Custom Event Pages"),
            description: t("Branded, SEO-optimised, and beautiful"),
          },
          {
            title: t("Multi-Event Dashboard"),
            description: t(
              "Manage multiple events/companies from one account."
            ),
          },
          {
            title: t("Mobile & Cloud Based"),
            description: t("Available on iOS and Android apps – always synced"),
          },
        ],
      },
    ],
  };

  return (
    <section className="relative py-16 bg-magnolia space-y-20">
      <div className="max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto space-y-20 px-6">
        {featuresData.map((feature, index) => {
          const isImageRight = feature.imageRight;
          return (
            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Left Column */}
              <div
                className={`relative ${
                  !isImageRight ? "order-1" : ""
                } space-y-6 xl:space-y-8 2xl:space-y-10`}
              >
                <h2
                  className="text-2xl xl:text-3xl 3xl:text-4xl capitalize text-deep-purple max-w-2xl w-full"
                  dangerouslySetInnerHTML={{ __html: feature.title }}
                />

                {/* Feature Points Section */}
                {feature.points && (
                  <div className="flex flex-col gap-5">
                    {feature.points.map((point, idx) => (
                      <div key={idx} className="flex items-center gap-5">
                        <div className="w-16 xl:w-20 aspect-square overflow-hidden flex-shrink-0">
                          <Image src={point.icon} classes="object-contain" />
                        </div>
                        <div className="flex-shrink">
                          <div className="text-lg xl:text-xl text-deep-purple font-bold">
                            {point.heading}
                          </div>
                          <div className="text-lg xl:text-xl text-deep-purple">
                            <p>{point.text}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* HTML List (dangerouslySetInnerHTML) */}
                {feature.listHTML && (
                  <div
                    className="text-lg xl:text-xl 2xl:text-2xl text-text-secondary font-medium description"
                    dangerouslySetInnerHTML={{ __html: feature.listHTML }}
                  />
                )}

                {/* Text and Button */}
                {feature.text && (
                  <div className="text-deep-purple text-lg xl:text-xl 2xl:text-2xl space-y-2">
                    {feature.text.map((t, i) => (
                      <p key={i} dangerouslySetInnerHTML={{ __html: t }} />
                    ))}
                  </div>
                )}

                {feature.hasButton && (
                  <div className="relative flex">
                    <Button
                      buttonLabel={t("get started")}
                      buttonLabelClasses="text-lg xl:text-xl font-semibold uppercase"
                      buttonClasses="px-20 h-12 2xl:h-16 bg-secondary-gradient"
                    />
                  </div>
                )}
              </div>

              {/* Right Column: Image */}
              <div className="relative">
                <div
                  className={`max-w-sm xl:max-w-md 2xl:max-w-lg w-full aspect-[21/19] overflow-hidden ${
                    isImageRight ? "ml-auto rounded-r-full" : ""
                  }`}
                >
                  <Image
                    src={feature.image}
                    width={"100%"}
                    height={"100%"}
                    alt={""}
                    classes={index === 2 ? "object-cover" : "object-contain"}
                    effect={"blur"}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto px-6">
        <h2
          className="text-deep-purple text-2xl xl:text-3xl capitalize font-semibold mb-10"
          dangerouslySetInnerHTML={{ __html: toolsSectionData.title }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 2xl:gap-10">
          {toolsSectionData.tools.map((tool, index) => (
            <div
              key={index}
              className="relative px-10 py-10 bg-white shadow-xl rounded-2xl overflow-hidden"
            >
              <div className="h-20 xl:h-24 2xl:h-28 aspect-[3/1] mx-auto mb-5 xl:mb-10">
                <Image src={tool.icon} classes={"object-contain"} />
              </div>
              <div className="flex flex-col gap-5">
                {tool.features.map((feature, i) => (
                  <div key={i} className="relative">
                    <div className="text-deep-purple text-base xl:text-lg 2xl:text-xl font-bold capitalize">
                      {feature.title}
                    </div>
                    <div className="text-deep-purple text-base xl:text-lg 2xl:text-xl">
                      {feature.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeEvent;
